import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { auditTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { TRANSLATOR, Translator } from '@miq/utils';
import { AreaUrl } from '../stacking-plan-container/floor-container/floor/item/details-overlay/details-overlay.constants';
import { StackPlanViewStateService } from '../state/sp-view-state.service';
import { getTranslationInput } from '../shared/utils/functions';
import { StackingPlanService } from '../stacking-plan.service';
import { Filters, FilterState, FilterView, ToolbarFilterOption, ToolBarViewOption } from './filters-toolbar.model';
import { DEFAULT_VIEW_OPTIONS } from './filters-toolbar.const';
import { FiltersService } from './filters.service';
import { LocalizationOptionsService } from '../localization-option.service';

@UntilDestroy()
@Component({
  selector: 'sp-filters-toolbar',
  templateUrl: './filters-toolbar.component.html',
  styleUrls: ['./filters-toolbar.component.scss'],
  standalone: false,
})
export class FiltersToolbarComponent implements OnDestroy, OnInit {
  /** Catalog of views */
  public viewOptions: ToolBarViewOption[];
  /**
   * Catalog of filter values. Should dynamically get values for specific views
   */
  public filterOptions: ToolbarFilterOption[];
  public displayFilterOptions: ToolbarFilterOption[];
  /**
   * Holds selected view. It is updated & initialized on constructor using the
   * filter state value
   */
  public selectedView: FilterView = FilterView.LeaseYear;
  /**
   * Holds all the selected filter values. It is updated & initialized on
   * constructor using the filter state value
   */
  public selectedFilters: Filters;
  public displayShowMore = false;
  public showMore = true;

  private normalizedNaicData = [];

  constructor(
    private filtersService: FiltersService,
    private stackingPlanService: StackingPlanService,
    @Inject(TRANSLATOR) private translationService: Translator,
    private spStateViewService: StackPlanViewStateService,
    private localizationOptionsService: LocalizationOptionsService
  ) {}

  ngOnInit(): void {
    this.viewOptions = DEFAULT_VIEW_OPTIONS;
    this.filtersService.filterState$.pipe(untilDestroyed(this)).subscribe(this._updateFilterStateAndOptions);

    this.spStateViewService.onStateChange$
      .pipe(
        untilDestroyed(this),
        auditTime(250),
        distinctUntilChanged((prev, curr) => prev.propertyType === curr.propertyType && prev.id === curr.id)
      )
      .subscribe(() => this.getFilterData());

    // listen to usage type change and reset filter to get new filter data
    this.filtersService.resetFilter$.pipe(untilDestroyed(this)).subscribe(() => this.getFilterData());
  }

  private _updateFilterStateAndOptions = (filterState: FilterState) => {
    if (filterState.viewBy !== FilterView.Industry) {
      this.setFilterState(filterState);
      return;
    }
    this.stackingPlanService.getLovValues('/lookup/naics').subscribe(result => {
      const labels = filterState.filterOptions.map(({ label }) => label);
      if (result?.length > 0 && labels) {
        if (this.normalizedNaicData.length === 0) {
          this.normalizeNaicData(result);
        }
        filterState.filterOptions.forEach(options => {
          options.label = this.normalizedNaicData[options.label] || options.label;
        });
      }
      this.setFilterState(filterState);
    });
  };

  ngOnDestroy(): void {
    /* required by untilDestroyed */
  }

  /**
   * Update view value on filter state
   */
  public onViewChange(selectedView: FilterView) {
    this.selectedView = selectedView;
    this.filtersService.clearSelectedFilters();
    this.getFilterData();
  }

  /**
   * Toggle clicked filter on filter state
   */
  public onFilterSelection(filterKey: number) {
    this.filtersService.toggleFilter(filterKey);
  }

  public hideShowFilters(showAll: boolean) {
    this.showMore = !this.showMore;
    this.displayFilterOptions = showAll ? [...this.filterOptions] : [...this.filterOptions.slice(0, 6)];
  }

  /**
   * Get filter data based on selected view from backend
   */
  private getFilterData = () => {
    this.filterOptions = [];
    const localeId = this.localizationOptionsService.getInstance().localeId;
    const options = { view: this.selectedView, localeId };
    const propertyId = this.spStateViewService.getState().id;
    this.filtersService
      .getFilterData(propertyId, options)
      .pipe(
        filter(Boolean),
        switchMap(filters => {
          return this.stackingPlanService.getLovValues(AreaUrl).pipe(
            map(mappedUomLov => {
              return filters.map(option => {
                let inputValue = null;
                if (this.selectedView === FilterView.LeaseYear) {
                  inputValue = { year: getTranslationInput(option.key) };
                }
                let spaceAreaUOM: string;
                const key = option.key ? this.translationService.translate(option.key, inputValue) : option.key;
                if (option.spaceAreaUOM) {
                  const value = mappedUomLov.find(e => e.key == option.spaceAreaUOM || e.value == option.spaceAreaUOM);
                  spaceAreaUOM = value ? String(value.key || value.value) : null;
                }
                return { ...option, key, spaceAreaUOM };
              });
            })
          );
        })
      )
      .subscribe(filters => this.filtersService.setFilterOptions(filters, this.selectedView));
  };

  private setFilterState(filterState: FilterState): void {
    this.filterOptions = filterState.filterOptions || [];
    //show more button will be shown when options are more than 6
    this.displayShowMore = this.filterOptions.length > 6;
    if (!this.displayShowMore || (this.displayShowMore && this.showMore)) {
      this.displayFilterOptions = [...this.filterOptions];
    } else {
      this.displayFilterOptions = [...this.filterOptions.slice(0, 6)];
    }
    this.selectedFilters = filterState.selectedFilters;
  }

  private normalizeNaicData(data: any): void {
    data.forEach(({ key, value, children }) => {
      if (children) {
        this.normalizeNaicData(children);
      }
      this.normalizedNaicData[key] = value;
    });
  }
}
